import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff, User, Lock, Save, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

function PasswordField({ label, value, onChange, show, onToggle, id, testId }) {
  return (
    <div>
      <Label htmlFor={id}>{label}</Label>
      <div className="relative mt-1">
        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          id={id}
          type={show ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="pl-10 pr-10"
          placeholder="••••••••"
          data-testid={testId}
        />
        <button
          type="button"
          onClick={onToggle}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
          tabIndex={-1}
        >
          {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}

async function apiRequest(endpoint, options = {}) {
  const res = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || 'Error');
  return data;
}

export default function ProfileDialog({ user, open, onClose }) {
  const [name, setName] = useState(user?.full_name || user?.name || '');
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);
  const [showConfirmPw, setShowConfirmPw] = useState(false);
  const [savingName, setSavingName] = useState(false);
  const [savingPw, setSavingPw] = useState(false);
  const [pwError, setPwError] = useState('');

  if (!open) return null;

  const handleSaveName = async () => {
    setSavingName(true);
    try {
      await apiRequest('/api/auth/me', {
        method: 'PATCH',
        body: JSON.stringify({ full_name: name, name }),
      });
      toast.success("Nom actualitzat correctament");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSavingName(false);
    }
  };

  const handleChangePassword = async () => {
    setPwError('');
    if (newPw.length < 6) { setPwError("La nova contrasenya ha de tenir almenys 6 caràcters"); return; }
    if (newPw !== confirmPw) { setPwError("Les contrasenyes no coincideixen"); return; }

    setSavingPw(true);
    try {
      await apiRequest('/api/auth/change-password', {
        method: 'POST',
        body: JSON.stringify({ current_password: currentPw, new_password: newPw }),
      });
      toast.success("Contrasenya canviada correctament");
      setCurrentPw('');
      setNewPw('');
      setConfirmPw('');
    } catch (err) {
      setPwError(err.message);
    } finally {
      setSavingPw(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="profile-dialog">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#7B2D3F] to-[#9B4D5F] flex items-center justify-center">
              <span className="text-white font-bold text-lg">{user?.full_name?.charAt(0)?.toUpperCase()}</span>
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">Editar perfil</h2>
              <p className="text-sm text-gray-500">{user?.email}</p>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Name section */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Informació personal</h3>
            <div>
              <Label htmlFor="profile-name">Nom complet</Label>
              <div className="relative mt-1">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  id="profile-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="pl-10"
                  data-testid="profile-name-input"
                />
              </div>
            </div>
            <Button
              onClick={handleSaveName}
              disabled={savingName || name === (user?.full_name || user?.name)}
              className="w-full bg-[#7B2D3F] hover:bg-[#5B1D2F] text-white"
              data-testid="profile-save-name-btn"
            >
              {savingName ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Desar nom
            </Button>
          </div>

          <div className="border-t border-gray-100" />

          {/* Password section */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Canviar contrasenya</h3>

            <PasswordField
              label="Contrasenya actual"
              value={currentPw}
              onChange={setCurrentPw}
              show={showCurrentPw}
              onToggle={() => setShowCurrentPw(!showCurrentPw)}
              id="current-password"
              testId="profile-current-password"
            />
            <PasswordField
              label="Nova contrasenya"
              value={newPw}
              onChange={setNewPw}
              show={showNewPw}
              onToggle={() => setShowNewPw(!showNewPw)}
              id="new-password"
              testId="profile-new-password"
            />
            <PasswordField
              label="Confirmar nova contrasenya"
              value={confirmPw}
              onChange={setConfirmPw}
              show={showConfirmPw}
              onToggle={() => setShowConfirmPw(!showConfirmPw)}
              id="confirm-password"
              testId="profile-confirm-password"
            />

            {pwError && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700">
                {pwError}
              </div>
            )}

            <Button
              onClick={handleChangePassword}
              disabled={savingPw || !currentPw || !newPw || !confirmPw}
              className="w-full bg-[#7B2D3F] hover:bg-[#5B1D2F] text-white"
              data-testid="profile-change-password-btn"
            >
              {savingPw ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Lock className="w-4 h-4 mr-2" />}
              Canviar contrasenya
            </Button>
          </div>
        </div>

        <div className="p-4 border-t border-gray-100 flex justify-end">
          <Button variant="ghost" onClick={onClose} data-testid="profile-close-btn">
            Tancar
          </Button>
        </div>
      </div>
    </div>
  );
}
